/* eslint-disable react/prop-types */
import { HolderOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, message } from 'antd';
import { useContext, useState } from 'react';
import {
  GeoLayer, Workspace,
} from 'types/geoserver.types';
import MainAppContext from 'contexts/MainAppContext';
import MapViewContext from 'contexts/MapViewContext';
import ProductsContext from 'contexts/ProductsContext';
import { List } from 'react-movable';
import { createLogger } from 'services/debugLogger';
import Product from './components/Product';
import styles from './index.module.scss';
import { getCapabilities } from '../../../../../../../../../../services/geoserver/outboundsRequest';
import { useLazyGetWorkspacesQuery } from '../../../../../../../../../../services/geoserver';

const logger = createLogger('ProductsWindow');

const { footer } = styles;

function ProductsWindow() {
  const [isLoadingProduct, setIsLoadingProduct] = useState(false);

  const {
    addNewProduct,
    selectedProducts,
    workspaces,
    moveProduct,
    activeProducts,
    capabilities,
    setCapabilities,
  } = useContext(ProductsContext);

  const { viewMode } = useContext(MainAppContext);
  const { index, moveLayer } = useContext(MapViewContext);
  const [getWorkspaces] = useLazyGetWorkspacesQuery();

  const getFirstValue = (data: Workspace[]) => {
    let firstValue;
    let index = 0;
    while (!firstValue && data.length > 0) {
      if (data[index] && data[index]?.layers?.[0]) {
        firstValue = { value: `${data[index].name}:${data[index].layers?.[0]?.name || ''}`, layer: data[index].layers?.[0] };
      }
      index += 1;
    }
    return firstValue;
  };

  const firstValue = getFirstValue(workspaces);

  return (
    <>
      {viewMode === 'layer' ? (
        <List
          values={selectedProducts}
          onChange={({ oldIndex, newIndex }) => {
            moveProduct(oldIndex, newIndex);
            if (activeProducts.length > 0) { moveLayer(oldIndex, newIndex); }
          }}
          renderList={({ children, props }) => (
            <ul
              {...props}
              style={{
                listStyle: 'none',
                paddingLeft: 0,
              }}
            >
              {children}
            </ul>
          )}
          renderItem={({
            value, props, isDragged, index,
          }) => {
            const { key, ...restProps } = props;
            return (
              <li
                key={key}
                {...restProps}
                style={{
                  ...restProps.style,
                  cursor: isDragged ? 'grabbing' : 'grab',
                  zIndex: isDragged ? 1500 : 0,
                  position: isDragged ? 'absolute' : 'relative',
                  listStyle: 'none',
                }}
              >
                <HolderOutlined data-movable-handle style={{ position: 'absolute', top: '52px', left: '5px' }} />
                <Product product={value} index={index || 0} key={value.id} />
              </li>
            );
          }}
        />
      ) : selectedProducts[index] && <Product product={selectedProducts[index]} index={index} />}
      {selectedProducts.length < 4 && (
      <div className={footer}>
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          loading={isLoadingProduct}
          disabled={isLoadingProduct}
          onClick={async () => {
            if (isLoadingProduct || selectedProducts.length >= 4) {
              return;
            }

            setIsLoadingProduct(true);
            try {
              if (!capabilities) {
                try {
                  const caps = await getCapabilities();
                  if (caps) {
                    setCapabilities(caps);
                    await getWorkspaces({});
                  }
                } catch (error) {
                  logger.error('GeoServer unavailable when adding product', error);
                  if (!firstValue) {
                    message.error('No products available. Layer providers are unreachable.');
                    return;
                  }
                  message.warning('GeoServer is unavailable. Showing only DTN layers.');
                }
              }

              if (firstValue) {
                addNewProduct(firstValue.value, firstValue.layer as GeoLayer);
              } else {
                logger.error('No products available. Check workspaces configuration.');
              }
            } finally {
              setIsLoadingProduct(false);
            }
          }}
        >
          Add product
        </Button>
      </div>
      )}
    </>
  );
}

export default ProductsWindow;
